import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot } from "@angular/router";
import { CampaignType } from "@front/m19-api-client";
import { provideCampaignType, StrategyService } from "@front/m19-injection-token";
import { CampaignTypeWithTacos } from "@front/m19-models";
import { SbStrategyService, SdStrategyService, SpStrategyService } from "@front/m19-services";
import { ProfileComponent } from "@m19-board/auth/profile-page/profile.component";
import { DataWarehouseComponent } from "@m19-board/data-warehouse/data-warehouse/data-warehouse.component";
import { BoardType, LayoutSelectorService } from "@m19-board/layout-selector.service";
import { ProfitAndLossComponent } from "@m19-board/profit-and-loss/profit-and-loss.component";
import { PromoDaysBoostComponent } from "@m19-board/strategies/promo-days-boost/promo-days-boost.component";
import { SbCreateStrategyPage } from "@m19-board/strategies/sb-create-strategy/pages/sb-create-strategy-page.component";
import { SbNewAdLinePage } from "@m19-board/strategies/sb-create-strategy/pages/sb-new-ad-line-page.component";
import { SbTacosActivationPage } from "@m19-board/strategies/sb-create-strategy/pages/sb-tacos-activation-page.component";
import { SbCreativeEditPageComponent } from "@m19-board/strategies/sb-create-strategy/pages/sb-creative-edit-page.component";
import { strategyGroupExistsGuard } from "@m19-board/strategies/strategy-page/strategy-group-exists.guard";
import { strategyGroupPageGuard } from "@m19-board/strategies/strategy-page/strategy-group-page.guard";
import {
  sbStrategyPageResolver,
  sdStrategyPageResolver,
  spStrategyPageResolver,
} from "@m19-board/strategies/strategy-page/strategy-page.resolver";
import { getStrategyPageRoutes } from "@m19-board/strategies/strategy-page/strategy-page.routes";
import { substrategyPageResolver } from "@m19-board/strategies/strategy-page/substrategy-page-resolver";
import { M19Route } from "@m19-board/utils/M19Route";
import { ActivitiesComponent } from "./activities/activities.component";
import { HomeComponent } from "./agency-board/home.component";
import { BrandAliasComponent } from "./brand/brand-alias.component";

import { PromoBoostBulkUploadPageComponent } from "./bulk-upload/promo-boost-bulk-upload-page/promo-boost-bulk-upload-page.component";
import { SdBulkUploadPageComponent } from "./bulk-upload/sd-bulk-upload-page/sd-bulk-upload-page.component";
import { SpBulkUploadPageComponent } from "./bulk-upload/sp-bulk-upload-page/sp-bulk-upload-page.component";
import { TacosBulkUploadPageComponent } from "./bulk-upload/tacos-bulk-upload-page/tacos-bulk-upload-page.component";
import { CatalogComponent } from "./catalog/catalog-sections/catalog.component";
import { CatalogUploadComponent } from "./catalog/catalog-upload.component";
import { DspAdvertiserRequestPageComponent } from "./dsp/dsp-advertiser-request-page/dsp-advertiser-request-page.component";
import { accountsSettingsGuard } from "./guards/accounts-settings/accounts-settings.guard";
import { canRequestAdvertiserGuard } from "./guards/dsp-guard/can-request-advertiser.guard";
import { AdvertisingStatsComponent } from "./insights/advertising-stats/advertising-stats.component";
import { HourlyPageComponent } from "./insights/hourly-view/hourly-page/hourly-page.component";
import { InventoryComponent } from "./inventory/inventory.component";
import { KeywordTrackerDetailsComponent } from "./keyword-tracker/keyword-tracker-details/keyword-tracker-details.component";
import { KeywordTrackerPageComponent } from "./keyword-tracker/keyword-tracker-page.component";
import { AccountLinkingPageComponent } from "./onboarding/account-linking-page.component";
import { ProductAnalysisPageComponent } from "./product-analysis/product-analysis-page.component";
import { SegmentsComponent } from "./segments/segment-manager.component";
import { AccountListComponent } from "./settings/account-settings/account-list/account-list.component";
import { AccountSettingsComponent } from "./settings/account-settings/account-settings.component";
import { AmazonInvoicesComponent } from "./settings/amazon-invoices/amazon-invoices.component";
import { BillingSettingsComponent } from "./settings/billing-settings/billing-settings.component";
import { SelfServiceFreemiumComponent } from "./settings/billing-settings/selfservice-freemium/selfservice-freemium.component";
import { UserManagementComponent } from "./settings/user-management/user-management.component";
import { ShareOfVoiceComponent } from "./share-of-voice/share-of-voice.component";
import { SbManagerComponent } from "./sponsored-brand/sb-manager/sb-manager.component";
import { SdManagerComponent } from "./sponsored-display/sd-manager/sd-manager.component";
import { SdStrategyCreationPageComponent } from "./sponsored-display/sd-manager/sd-strategy-creation/sd-strategy-creation-page.component";
import { SpManagerComponent } from "./sponsored-product/sp-manager/sp-manager.component";
import { SpStrategyGroupPageComponent } from "./sponsored-product/sp-page/strategy-group/sp-strategy-group-page.component";
import { ProductGroupPageComponent } from "./strategies/product-group/product-group-page/product-group-page.component";
import { ProductGroupComponent } from "./strategies/product-group/product-group.component";
import { CreateTacosStrategyComponent } from "./strategies/tacos-strategy/create-tacos-strategy.component";
import { TacosStrategyPageComponent } from "./strategies/tacos-strategy/tacos-strategy-page.component";
import { TacosManagerComponent } from "./tacos/tacos-manager.component";
import { TrafficStatsComponent } from "./traffic-stats/traffic-stats.component";
import { readOnlyGuarded } from "./guards/read-only/read-only.guard";
import { SpStrategyGroupCreationComponent } from "@m19-board/sponsored-product/sp-creation/sp-strategy-creation/sp-strategy-group-creation.component";
import { SpStrategyCreationComponent } from "@m19-board/sponsored-product/sp-page/sp-substrategy-creation/sp-strategy-creation.component";
import { Account360ProductTourOverlayComponent } from "@m19-board/overlay/overlays/product-tour/account360-product-tour-overlay.component";
import { TacosProductTourOverlayComponent } from "@m19-board/overlay/overlays/product-tour/tacos-product-tour-overlay.component";
import { SbProductTourOverlayComponent } from "@m19-board/overlay/overlays/product-tour/sb-product-tour-overlay.component";
import { SdProductTourOverlayComponent } from "@m19-board/overlay/overlays/product-tour/sd-product-tour-overlay.component";
import { AmcProductTourOverlayComponent } from "@m19-board/overlay/overlays/product-tour/amc-product-tour-overlay.component";
import { Product360ProductTourOverlayComponent } from "@m19-board/overlay/overlays/product-tour/product360-product-tour-overlay.component";
import { TrafficAnalysisProductTourOverlayComponent } from "@m19-board/overlay/overlays/product-tour/traffic-analysis-product-tour-overlay.component";
import { accountStatusOverlays, apiAccessOverlays } from "@m19-board/overlay/overlays/overlay-lists";
import { HourlyUnsupportedMarketplaceOverlayComponent } from "@m19-board/overlay/overlays/hourly-unsupported-marketplace-overlay.component";
import { HourlyNotInBillingPlanOverlayComponent } from "@m19-board/overlay/overlays/hourly-not-in-billing-plan-overlay.component";
import { SpBidderOffOverlayComponent } from "@m19-board/overlay/overlays/bidder-off/sp-bidder-off-overlay.component";
import { SbBidderOffOverlayComponent } from "@m19-board/overlay/overlays/bidder-off/sb-bidder-off-overlay.component";
import { SdBidderOffOverlayComponent } from "@m19-board/overlay/overlays/bidder-off/sd-bidder-off-overlay.component";
import { TacosBidderOffOverlayComponent } from "@m19-board/overlay/overlays/bidder-off/tacos-bidder-off-overlay.component";
import { ProfitAndLossVendorNotSupportedOverlayComponent } from "@m19-board/overlay/overlays/profit-and-loss-vendor-not-supported-overlay.component";
import { SbNotSupportedForKdpOverlayComponent } from "@m19-board/overlay/overlays/sb-not-supported-for-kdp-overlay.component";
import { SdNotSupportedForKdpOverlayComponent } from "@m19-board/overlay/overlays/sd-not-supported-for-kdp-overlay.component";
import { KeywordTrackerNotSupportedOverlayComponent } from "@m19-board/overlay/overlays/keyword-tracker-not-supported-overlay.component";
import { ShareOfVoiceNotSupportedOverlayComponent } from "@m19-board/overlay/overlays/share-of-voice-not-supported-overlay.component";
import { AdvertisingApiAccessLostOverlayComponent } from "@m19-board/overlay/overlays/advertising-api-access-lost-overlay.component";
import { TacosStartFreeTestOverlayComponent } from "@m19-board/overlay/overlays/start-free-test/tacos-start-free-test-overlay.component";
import { SpStartFreeTestOverlayComponent } from "@m19-board/overlay/overlays/start-free-test/sp-start-free-test-overlay.component";
import { SbStartFreeTestOverlayComponent } from "@m19-board/overlay/overlays/start-free-test/sb-start-free-test-overlay.component";
import { SdStartFreeTestOverlayComponent } from "@m19-board/overlay/overlays/start-free-test/sd-start-free-test-overlay.component";

export const SP_STRATEGIES_URL = "/advertising/sponsored-product";
export const SB_STRATEGIES_URL = "/advertising/sponsored-brands";
export const SD_STRATEGIES_URL = "/advertising/sponsored-display";
export const TACOS_STRATEGIES_URL = "/advertising/tacos-strategies";

export const onlyWhiteLabelSelfServiceGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot,
) => {
  const layoutService = inject(LayoutSelectorService);
  return layoutService.getBoardType() === BoardType.WHITELABEL_SELFSERVICE;
};

export const dashboardRoutes: M19Route[] = [
  {
    path: "dashboard",
    children: [
      {
        path: "home",
        component: HomeComponent,
        data: {
          parentTitle: "page-title.home",
          withDatePicker: true,
          withCurrencySelector: true,
          withMultiAccountSelector: true,
        },
      },
      {
        path: "account360",
        loadChildren: () =>
          import("./account360/account360.routes").then((r) => r.A360Routes("/dashboard/account360/dashboard-list")),
        data: {
          parentTitle: "page-title.account_360",
          overlays: [Account360ProductTourOverlayComponent, AdvertisingApiAccessLostOverlayComponent],
        },
      },
      {
        path: "advertising",
        component: AdvertisingStatsComponent,
        data: {
          parentTitle: "page-title.advertising",
          withDatePicker: true,
          withCurrencySelector: true,
          withAccountSelector: true,
          withChatbotSafeMargin: true,
          overlays: [AdvertisingApiAccessLostOverlayComponent],
        },
      },
      {
        path: "profit-and-loss",
        component: ProfitAndLossComponent,
        data: {
          parentTitle: "page-title.profit_and_loss",
          withAccountSelector: true,
          withCurrencySelector: true,
          overlays: [ProfitAndLossVendorNotSupportedOverlayComponent, ...apiAccessOverlays()],
        },
      },
      {
        path: "advertiser-creation-request",
        component: DspAdvertiserRequestPageComponent,
        data: {
          parentTitle: "page-title.dsp_advertiser_creation_request",
          withAccountSelector: true,
        },
        canActivate: [canRequestAdvertiserGuard],
      },
      { path: "**", redirectTo: "home" },
    ],
  },
  {
    path: "bulk",
    ...readOnlyGuarded,
    children: [
      {
        path: "sponsored-product",
        component: SpBulkUploadPageComponent,
        data: {
          title: "bulk-upload.bulk_upload",
          parentTitle: "strategy-grid.sponsored_product",
          parentRouteUrl: SP_STRATEGIES_URL,
          padded: false,
          subPage: true,
        },
      },
      {
        path: "sponsored-display",
        component: SdBulkUploadPageComponent,
        data: {
          title: "bulk-upload.bulk_upload",
          parentTitle: "strategy-grid.sponsored_display",
          parentRouteUrl: SD_STRATEGIES_URL,
          padded: false,
          subPage: true,
        },
      },
      {
        path: "promo-boost",
        component: PromoBoostBulkUploadPageComponent,
        data: {
          title: "bulk-upload.bulk_upload",
          parentTitle: "page-title.promo_days_boost",
          parentRouteUrl: "/advertising/promo-days-boost",
          padded: false,
          subPage: true,
        },
      },
      {
        path: "tacos",
        component: TacosBulkUploadPageComponent,
        data: {
          title: "bulk-upload.bulk_upload",
          parentTitle: "page-title.tacos",
          parentRouteUrl: TACOS_STRATEGIES_URL,
          padded: false,
          subPage: true,
        },
      },
    ],
  },
  {
    path: "advertising",
    children: [
      {
        path: "tacos-strategies",
        providers: [provideCampaignType(CampaignTypeWithTacos.TACOS)],
        data: {
          overlays: [
            TacosProductTourOverlayComponent,
            ...accountStatusOverlays({
              bidderOff: TacosBidderOffOverlayComponent,
              startFreeTest: TacosStartFreeTestOverlayComponent,
            }),
          ],
        },
        children: [
          {
            path: "",
            component: TacosManagerComponent,
            data: {
              parentTitle: "page-title.tacos",
              withAccountSelector: true,
              withChatbotSafeMargin: true,
              padded: false,
              overlays: [
                TacosProductTourOverlayComponent,
                ...apiAccessOverlays(),
                ...accountStatusOverlays({
                  bidderOff: TacosBidderOffOverlayComponent,
                  startFreeTest: TacosStartFreeTestOverlayComponent,
                }),
              ],
            },
          },
          {
            path: "create",
            component: CreateTacosStrategyComponent,
            ...readOnlyGuarded,
            data: {
              parentTitle: "page-title.tacos",
              parentRouteUrl: TACOS_STRATEGIES_URL,
              title: "tacos-strategy.create_tacos_strategy",
              withAccountSelector: true,
              subPage: true,
              padded: false,
              withChatbotSafeMargin: true,
            },
          },
          {
            path: ":tacosId/sb/new-ad-line",
            component: SbNewAdLinePage,
            ...readOnlyGuarded,
            data: {
              parentTitle: "page-title.tacos",
              parentRouteUrl: TACOS_STRATEGIES_URL,
              subPage: true,
              padded: false,
              withChatbotSafeMargin: true,
            },
          },
          {
            path: ":tacosId/sb/:strategyId/:creativeId/assets",
            component: SbCreativeEditPageComponent,
            data: {
              step: "assets",
              parentTitle: "page-title.tacos",
              parentRouteUrl: TACOS_STRATEGIES_URL,
              subPage: true,
              padded: false,
              withChatbotSafeMargin: true,
            },
          },
          {
            path: ":tacosId/sb/:strategyId/:creativeId/asins",
            component: SbCreativeEditPageComponent,
            data: {
              step: "asins",
              parentTitle: "page-title.tacos",
              parentRouteUrl: TACOS_STRATEGIES_URL,
              subPage: true,
              padded: false,
              withChatbotSafeMargin: true,
            },
          },
          {
            path: ":tacosId/sb",
            component: SbTacosActivationPage,
            ...readOnlyGuarded,
            data: {
              parentTitle: "page-title.tacos",
              parentRouteUrl: TACOS_STRATEGIES_URL,
              subPage: true,
              padded: false,
              withChatbotSafeMargin: true,
            },
          },
          {
            path: ":tacosId",
            component: TacosStrategyPageComponent,
            data: {
              parentTitle: "page-title.tacos",
              parentRouteUrl: TACOS_STRATEGIES_URL,
              withDatePicker: true,
              withCurrencySelector: true,
              subPage: true,
            },
          },
        ],
      },
      {
        path: "sponsored-product",
        providers: [
          provideCampaignType(CampaignType.SP),
          {
            provide: StrategyService,
            useExisting: SpStrategyService,
          },
        ],
        data: {
          overlays: accountStatusOverlays({
            bidderOff: SpBidderOffOverlayComponent,
            startFreeTest: SpStartFreeTestOverlayComponent,
          }),
        },
        children: [
          {
            path: "",
            component: SpManagerComponent,
            data: {
              parentTitle: "strategy-grid.sponsored_product",
              withAccountSelector: true,
              padded: false,
            },
          },
          {
            path: "create",
            component: SpStrategyGroupCreationComponent,
            ...readOnlyGuarded,
            data: {
              parentTitle: "strategy-grid.sponsored_product",
              title: "strategy-group-migration-page.new_strategy_group",
              parentRouteUrl: SP_STRATEGIES_URL,
              subPage: true,
              withAccountSelector: true,
              padded: false,
              withChatbotSafeMargin: true,
            },
          },
          {
            path: "strategy-group",
            children: [
              {
                path: ":id",
                component: SpStrategyGroupPageComponent,
                data: {
                  parentTitle: "strategy-grid.sponsored_product",
                  parentRouteUrl: SP_STRATEGIES_URL,
                  withDatePicker: true,
                  withCurrencySelector: true,
                  subPage: true,
                },
                canActivate: [strategyGroupPageGuard],
              },
              {
                path: ":id/create-strategy",
                component: SpStrategyCreationComponent,
                ...readOnlyGuarded,
                data: {
                  parentTitle: "strategy-grid.sponsored_product",
                  parentRouteUrl: SP_STRATEGIES_URL,
                  subPage: true,
                  padded: false,
                  withChatbotSafeMargin: true,
                },
                canActivate: [strategyGroupExistsGuard],
              },
              {
                path: ":groupId/:id",
                children: getStrategyPageRoutes(
                  CampaignType.SP,
                  "strategy-grid.sponsored_product",
                  SP_STRATEGIES_URL,
                  substrategyPageResolver,
                ),
              },
            ],
          },
          {
            path: ":id",
            children: getStrategyPageRoutes(
              CampaignType.SP,
              "strategy-grid.sponsored_product",
              SP_STRATEGIES_URL,
              spStrategyPageResolver,
            ),
          },
        ],
      },
      {
        path: "sponsored-brands",
        providers: [
          provideCampaignType(CampaignType.SB),
          {
            provide: StrategyService,
            useExisting: SbStrategyService,
          },
        ],
        data: {
          overlays: [
            SbProductTourOverlayComponent,
            SbNotSupportedForKdpOverlayComponent,
            ...accountStatusOverlays({
              bidderOff: SbBidderOffOverlayComponent,
              startFreeTest: SbStartFreeTestOverlayComponent,
            }),
          ],
        },
        children: [
          {
            path: "",
            component: SbManagerComponent,
            data: {
              parentTitle: "strategy-page.sponsored_brand",
              withAccountSelector: true,
              padded: false,
            },
          },
          {
            path: "create",
            component: SbCreateStrategyPage,
            ...readOnlyGuarded,
            data: {
              parentTitle: "strategy-page.sponsored_brand",
              parentRouteUrl: SB_STRATEGIES_URL,
              padded: false,
              withChatbotSafeMargin: true,
            },
          },
          {
            path: ":strategyId/new-ad-line",
            component: SbNewAdLinePage,
            ...readOnlyGuarded,
            data: {
              parentTitle: "strategy-page.sponsored_brand",
              parentRouteUrl: SB_STRATEGIES_URL,
              subPage: true,
              padded: false,
              withChatbotSafeMargin: true,
            },
          },
          {
            path: ":strategyId/:creativeId/assets",
            component: SbCreativeEditPageComponent,
            ...readOnlyGuarded,
            data: {
              step: "assets",
              subPage: true,
              parentTitle: "strategy-page.sponsored_brand",
              parentRouteUrl: SB_STRATEGIES_URL,
              padded: false,
              withChatbotSafeMargin: true,
            },
          },
          {
            path: ":strategyId/:creativeId/asins",
            component: SbCreativeEditPageComponent,
            ...readOnlyGuarded,
            data: {
              step: "asins",
              subPage: true,
              parentTitle: "strategy-page.sponsored_brand",
              parentRouteUrl: SB_STRATEGIES_URL,
              padded: false,
              withChatbotSafeMargin: true,
            },
          },
          {
            path: ":id",
            children: getStrategyPageRoutes(
              CampaignType.SB,
              "strategy-page.sponsored_brand",
              SB_STRATEGIES_URL,
              sbStrategyPageResolver,
            ),
          },
        ],
      },
      {
        path: "sponsored-display",
        providers: [
          provideCampaignType(CampaignType.SD),
          {
            provide: StrategyService,
            useExisting: SdStrategyService,
          },
        ],
        data: {
          overlays: [
            SdProductTourOverlayComponent,
            SdNotSupportedForKdpOverlayComponent,
            ...accountStatusOverlays({
              bidderOff: SdBidderOffOverlayComponent,
              startFreeTest: SdStartFreeTestOverlayComponent,
            }),
          ],
        },
        children: [
          {
            path: "",
            component: SdManagerComponent,
            data: {
              parentTitle: "strategy-grid.sponsored_display",
              withAccountSelector: true,
              padded: false,
            },
          },
          {
            path: "create",
            component: SdStrategyCreationPageComponent,
            ...readOnlyGuarded,
            data: {
              parentTitle: "strategy-grid.sponsored_display",
              title: "sd-strategy-creation.new_sponsored_display",
              parentRouteUrl: SD_STRATEGIES_URL,
              withAccountSelector: true,
              subPage: true,
              padded: false,
              withChatbotSafeMargin: true,
            },
          },
          {
            path: ":id",
            children: getStrategyPageRoutes(
              CampaignType.SD,
              "strategy-grid.sponsored_display",
              SD_STRATEGIES_URL,
              sdStrategyPageResolver,
            ),
          },
        ],
      },
      {
        path: "hourly-stats",
        component: HourlyPageComponent,
        data: {
          parentTitle: "page-title.hourly_stats",
          withAccountSelector: true,
          withDatePicker: true,
          withCurrencySelector: true,
          withChatbotSafeMargin: true,
          overlays: [
            HourlyUnsupportedMarketplaceOverlayComponent,
            HourlyNotInBillingPlanOverlayComponent,
            ...accountStatusOverlays(),
          ],
        },
      },
      {
        path: "promo-days-boost",
        component: PromoDaysBoostComponent,
        data: {
          parentTitle: "page-title.promo_days_boost",
          withAccountSelector: true,
          padded: false,
        },
      },
      {
        path: "dsp",
        providers: [provideCampaignType(CampaignType.DSP)],
        loadChildren: () => import("./dsp/dsp.routes").then((r) => r.DspRoutes),
      },
      {
        path: "amc",
        loadChildren: () => import("./amc/amc.routes").then((r) => r.AmcRoutes),
        data: {
          overlays: [AmcProductTourOverlayComponent],
        },
      },
    ],
  },
  {
    path: "product-center",
    children: [
      {
        path: "product360",
        loadChildren: () =>
          import("./product360/product360.routes").then((r) =>
            r.P360Routes("/product-center/product360/dashboard-list"),
          ),
        data: {
          parentTitle: "page-title.product_360",
          withChatbotSafeMargin: true,
          overlays: [Product360ProductTourOverlayComponent, AdvertisingApiAccessLostOverlayComponent],
        },
      },
      {
        path: "product-analysis",
        component: ProductAnalysisPageComponent,
        data: {
          parentTitle: "page-title.product_analysis",
          withAccountSelector: true,
          withDatePicker: true,
          withCurrencySelector: true,
          withChatbotSafeMargin: true,
          overlays: [AdvertisingApiAccessLostOverlayComponent],
        },
      },
      {
        path: "product-tracker",
        loadChildren: () => import("./product-tracker/product-tracker.routes").then((r) => r.routes),
        data: {
          parentTitle: "page-title.product_tracker",
          parentRouteUrl: "/product-center/product-tracker",
        },
      },
      {
        path: "inventory",
        component: InventoryComponent,
        data: {
          parentTitle: "page-title.inventory",
          withAccountSelector: true,
          withDatePicker: true,
          withCurrencySelector: true,
          overlays: apiAccessOverlays(),
        },
      },
      {
        path: "catalog",
        component: CatalogComponent,
        data: {
          parentTitle: "page-title.catalog",
          withAccountSelector: true,
          padded: false,
        },
      },
      {
        path: "catalog/upload",
        component: CatalogUploadComponent,
        ...readOnlyGuarded,
        data: {
          parentTitle: "page-title.catalog",
        },
      },
      {
        path: "product-group",
        component: ProductGroupComponent,
        data: {
          parentTitle: "page-title.product_groups",
          padded: false,
          withAccountSelector: true,
          withChatbotSafeMargin: true,
          overlays: [AdvertisingApiAccessLostOverlayComponent],
        },
      },
      {
        path: "product-group/:id",
        component: ProductGroupPageComponent,
        data: {
          parentTitle: "page-title.product_groups",
          parentRouteUrl: "/product-center/product-group",
          subPage: true,
        },
      },
      {
        path: "brand-alias",
        component: BrandAliasComponent,
        data: {
          parentTitle: "page-title.brand_alias",
          withAccountSelector: true,
          overlays: [AdvertisingApiAccessLostOverlayComponent],
        },
      },
    ],
  },
  {
    path: "keyword-center",
    children: [
      {
        path: "traffic-analysis",
        component: TrafficStatsComponent,
        data: {
          parentTitle: "page-title.traffic_analysis",
          withAccountSelector: true,
          withDatePicker: true,
          withCurrencySelector: true,
          overlays: [TrafficAnalysisProductTourOverlayComponent],
        },
      },
      {
        path: "segments",
        redirectTo: "segments/keyword-segments",
        pathMatch: "full",
        data: {
          parentTitle: "page-title.segments",
        },
      },
      {
        path: "segments/keyword-segments",
        component: SegmentsComponent,
        data: {
          parentTitle: "page-title.segments",
          withAccountSelector: true,
          withChatbotSafeMargin: true,
          overlays: [AdvertisingApiAccessLostOverlayComponent],
        },
      },
      {
        path: "segments/product-segments",
        component: SegmentsComponent,
        data: {
          parentTitle: "page-title.segments",
          withAccountSelector: true,
          withChatbotSafeMargin: true,
          overlays: [AdvertisingApiAccessLostOverlayComponent],
        },
      },
      {
        path: "share-of-voice",
        component: ShareOfVoiceComponent,
        data: {
          parentTitle: "page-title.share_of_voice",
          withDatePicker: true,
          withAccountSelector: true,
          overlays: [...accountStatusOverlays(), ShareOfVoiceNotSupportedOverlayComponent],
        },
      },
      {
        path: "keyword-tracker",
        component: KeywordTrackerPageComponent,
        data: {
          parentTitle: "page-title.keyword_tracker",
          withDatePicker: true,
          withAccountSelector: true,
          withChatbotSafeMargin: true,
          overlays: [...accountStatusOverlays(), KeywordTrackerNotSupportedOverlayComponent],
        },
      },
      {
        path: "keyword-tracker/:searchterm",
        component: KeywordTrackerDetailsComponent,
        data: {
          parentTitle: "page-title.keyword_tracker",
          parentRouteUrl: "/keyword-center/keyword-tracker",
          withDatePicker: true,
          subPage: true,
        },
      },
      // {
      //   path: "search-trends",
      //   component: SearchTermRanksComponent,
      //   data: {
      //     parentTitle: "page-title.search_trends",
      //     withDatePicker: true,
      //     withAccountSelector: true,
      //   },
      // },
    ],
  },
  {
    path: "activities",
    component: ActivitiesComponent,
    data: {
      parentTitle: "page-title.activities",
      withAccountSelector: true,
      withDatePicker: true,
      withChatbotSafeMargin: true,
    },
  },
  {
    path: "profile",
    component: ProfileComponent,
    data: {
      parentTitle: "page-title.my_profile",
    },
  },
  {
    path: "accounts",
    data: {
      parentTitle: "page-title.accounts",
      padded: false,
      withChatbotSafeMargin: true,
    },
    children: [
      {
        component: AccountSettingsComponent,
        canActivate: [accountsSettingsGuard],
        path: "",
      },
      {
        path: ":id",
        children: [
          {
            path: "",
            component: AccountListComponent,
            data: {
              parentTitle: "page-title.accounts",
              padded: false,
              subPage: true,
              parentRouteUrl: "accounts",
            },
          },
          {
            path: "account-linking",
            component: AccountLinkingPageComponent,
            title: "Link your Amazon accounts",
            data: {
              fromOnboarding: false,
            },
          },
        ],
      },
    ],
  },
  {
    path: "user-management",
    component: UserManagementComponent,
    data: {
      parentTitle: "page-title.user_management",
      withDatePicker: true,
      withChatbotSafeMargin: true,
    },
  },
  {
    path: "billing",
    component: BillingSettingsComponent,
    data: {
      parentTitle: "page-title.billing",
    },
  },
  {
    path: "freemium",
    component: SelfServiceFreemiumComponent,
    canActivate: [onlyWhiteLabelSelfServiceGuard],
    data: {
      parentTitle: "page-title.freemium",
    },
  },
  {
    path: "data-warehouse",
    component: DataWarehouseComponent,
    data: {
      parentTitle: "page-title.data_warehouse",
      withOrganizationSelector: true,
      withChatbotSafeMargin: true,
    },
  },
  {
    path: "amazon-invoices",
    component: AmazonInvoicesComponent,
    data: {
      parentTitle: "page-title.amazon_invoices",
      withAccountSelector: true,
    },
  },
];
